/**
 * Offscreen Canvas Manager
 * Orchestrates offscreen rendering. If OffscreenCanvas is supported by the user browser,
 * it will spin up bg.worker.ts and transfer control of the background canvas to the background worker.
 * If not supported (e.g., legacy mobile views), it provides a seamless fallback to direct main-thread drawing.
 */

import { SimulationConfig } from '@/types';

export class OffscreenCanvasManager {
  private static worker: Worker | null = null;
  private static isOffscreenSupported = false;
  private static isInitialized = false;

  public static checkSupport(): boolean {
    if (typeof window === 'undefined') return false;
    
    this.isOffscreenSupported = 
      typeof HTMLCanvasElement !== 'undefined' && 
      'transferControlToOffscreen' in HTMLCanvasElement.prototype &&
      typeof Worker !== 'undefined';
      
    return this.isOffscreenSupported;
  }

  public static initialize(
    canvas: HTMLCanvasElement | null,
    config: SimulationConfig,
    width: number,
    height: number,
    globalHue: number,
    isPlaying: boolean
  ): boolean {
    if (this.isInitialized) return this.isOffscreenSupported;
    this.isInitialized = true;

    if (!canvas || !this.checkSupport()) {
      console.log('OffscreenCanvas is not supported. Running main-thread canvas renderers as fallback.');
      this.isOffscreenSupported = false;
      return false;
    }

    try {
      // Create offscreen worker
      this.worker = new Worker(new URL('../bg.worker.ts', import.meta.url), { type: 'module' });
      
      // Transfer canvas ownership to the worker thread
      const offscreen = canvas.transferControlToOffscreen();
      
      this.worker.postMessage({
        type: 'INIT',
        payload: {
          canvas: offscreen,
          width,
          height,
          config,
          globalHue,
          isPlaying
        }
      }, [offscreen]);

      console.log('OffscreenCanvas background renderer successfully initialized on off-thread Web Worker.');
      return true;
    } catch (e) {
      console.warn('Failed to initialize OffscreenCanvas background worker. Falling back to on-thread rendering context.', e);
      this.isOffscreenSupported = false;
      if (this.worker) {
        this.worker.terminate();
        this.worker = null;
      }
      return false;
    }
  }

  public static setConfig(config: SimulationConfig, globalHue: number, isPlaying: boolean) {
    if (this.worker && this.isOffscreenSupported) {
      this.worker.postMessage({
        type: 'SET_CONFIG',
        payload: {
          config,
          globalHue,
          isPlaying
        }
      });
    }
  }

  public static resize(width: number, height: number) {
    if (this.worker && this.isOffscreenSupported) {
      this.worker.postMessage({
        type: 'RESIZE',
        payload: {
          width,
          height
        }
      });
    }
  }

  public static setPlaying(isPlaying: boolean) {
    if (this.worker && this.isOffscreenSupported) {
      this.worker.postMessage({
        type: 'SET_PLAYING',
        payload: {
          isPlaying
        }
      });
    }
  }

  public static setHue(hue: number) {
    if (this.worker && this.isOffscreenSupported) {
      this.worker.postMessage({
        type: 'SET_HUE',
        payload: {
          hue
        }
      });
    }
  }

  public static terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.isInitialized = false;
    this.isOffscreenSupported = false;
  }

  public static isUsingOffscreenWorker(): boolean {
    return this.isOffscreenSupported && this.worker !== null;
  }
}
